/**
 * AuthContext - Contesto Autenticazione
 * Il Diario di Bordo - Frontend
 *
 * MIGRAZIONE httpOnly Cookie:
 * - I token JWT sono gestiti via cookie httpOnly (impostati dal backend)
 * - Il frontend NON legge/scrive più token in localStorage
 * - checkAuth() chiama /auth/me (il cookie viene inviato automaticamente)
 * - logout() chiama /auth/logout (il backend cancella i cookie)
 *
 * FASE 5: Supporto per ruolo 'responsabile' e Organization
 */

import { createContext, useContext, useState, useEffect, useRef } from 'react'
import api from '../services/api'
import { useTheme } from './ThemeContext'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [tenant, setTenant] = useState(null)
  const [organization, setOrganization] = useState(null) // FASE 5: Organization state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Flag per 2FA setup obbligatorio
  const [needs2FASetup, setNeeds2FASetup] = useState(false)

  // Ref per prevenire chiamate multiple a 2FA status
  const twoFACheckDone = useRef(false)

  const { setColors } = useTheme()

  // Verifica autenticazione all'avvio
  useEffect(() => {
    checkAuth()
  }, [])

  // FIX: Registra dispositivo fidato dopo il reload (se richiesto durante login)
  useEffect(() => {
    const registerPendingDevice = async () => {
      const pending = localStorage.getItem('pendingTrustDevice')
      if (pending === 'true' && user) {
        console.log('[AuthContext] Registering pending trusted device...')
        localStorage.removeItem('pendingTrustDevice')

        try {
          const ua = navigator.userAgent
          let browserName = 'Browser'
          if (ua.includes('Chrome')) browserName = 'Chrome'
          else if (ua.includes('Firefox')) browserName = 'Firefox'
          else if (ua.includes('Safari')) browserName = 'Safari'
          else if (ua.includes('Edge')) browserName = 'Edge'

          await api.post('/auth/devices/trust', { name: browserName })
          console.log('[AuthContext] Device registered as trusted')
        } catch (err) {
          console.warn('[AuthContext] Failed to register trusted device:', err)
        }
      }
    }

    registerPendingDevice()
  }, [user])

  // Applica colori tenant quando cambia
  useEffect(() => {
    if (tenant?.settings?.branding) {
      const { primaryColor, secondaryColor } = tenant.settings.branding
      if (primaryColor || secondaryColor) {
        setColors(primaryColor, secondaryColor)
      }
    }
  }, [tenant])

  // Listener per errore 2FA_SETUP_REQUIRED
  useEffect(() => {
    const handle2FARequired = () => {
      setNeeds2FASetup(true)
    }

    window.addEventListener('2fa-setup-required', handle2FARequired)
    return () => window.removeEventListener('2fa-setup-required', handle2FARequired)
  }, [])

  /**
   * Check stato 2FA per coordinatori e responsabili
   * Chiamato solo una volta per sessione
   */
  const check2FAStatus = async (userData) => {
    // Evita chiamate multiple
    if (twoFACheckDone.current) return
    // Solo coordinatori e responsabili devono configurare 2FA
    if (userData.role !== 'coordinatore' && userData.role !== 'responsabile') return

    twoFACheckDone.current = true

    try {
      const status2FA = await api.get('/auth/2fa/status')
      if (!status2FA.data.data.enabled) {
        setNeeds2FASetup(true)
      }
    } catch (err) {
      console.error('2FA status check error:', err)
      // In caso di errore, non bloccare l'utente
    }
  }

  /**
   * FASE 5: Carica dati Organization per responsabile
   */
  const loadOrganization = async () => {
    try {
      const orgRes = await api.get('/organization/current')
      setOrganization(orgRes.data.data?.organization || orgRes.data.data)
    } catch (err) {
      console.error('Failed to load organization:', err)
      setOrganization(null)
    }
  }

  /**
   * checkAuth - Verifica sessione tramite cookie httpOnly
   * Il browser invia automaticamente il cookie con la richiesta
   */
  const checkAuth = async () => {
    try {
      console.log('[AuthContext] checkAuth - calling /auth/me')
      const response = await api.get('/auth/me')

      const userData = response.data.data.user
      const tenantData = response.data.data.tenant

      console.log('[AuthContext] /auth/me success:', userData.email)

      setUser(userData)
      setTenant(tenantData)

      // FASE 5: Se l'utente è responsabile, carica organization
      if (userData.role === 'responsabile') {
        await loadOrganization()
      }

      // Check 2FA status per coordinatori e responsabili (solo una volta)
      await check2FAStatus(userData)

      // Push: sync subscription residua (se utente diverso aveva una sub su questo browser)
      try {
        const { syncPushSubscriptionWithCurrentUser } = await import('../hooks/usePushNotifications')
        syncPushSubscriptionWithCurrentUser({ swallowErrors: true }) // fire-and-forget
      } catch { /* non-blocking */ }

    } catch (err) {
      console.error('[AuthContext] Auth check failed:', err.response?.status, err.message)
      // Nessun cookie valido o sessione scaduta — utente non autenticato
    } finally {
      setLoading(false)
    }
  }

  /**
   * Login - con supporto 2FA
   * I cookie httpOnly vengono impostati dal backend nella risposta
   */
  const login = async (email, password) => {
    try {
      setError(null)

      console.log('[AuthContext] Starting login for:', email)

      const response = await api.post('/auth/login', { email, password })

      console.log('[AuthContext] Login response:', {
        success: response.data.success,
        requires2FA: response.data.requires2FA,
        hasData: !!response.data.data
      })

      // CHECK 2FA REQUIRED
      if (response.data.requires2FA) {
        console.log('[AuthContext] 2FA required')
        return {
          requires2FA: true,
          data: response.data.data
        }
      }

      // Login normale senza 2FA — cookie httpOnly già impostati dal backend
      const data = response.data.data

      if (!data) {
        console.error('[AuthContext] Login response missing data field')
        return { success: false, error: 'Risposta del server non valida' }
      }

      const { user: userData, tenant: tenantData } = data

      if (!userData) {
        console.error('[AuthContext] Login response missing user data')
        return { success: false, error: 'Dati utente non ricevuti' }
      }

      console.log('[AuthContext] Login completed - cookies set by backend')

      return { success: true, user: userData, tenant: tenantData }
    } catch (err) {
      console.error('[AuthContext] Login error:', err.response?.data || err.message)
      const message = err.response?.data?.message || 'Errore durante il login'
      setError(message)
      return { success: false, error: message }
    }
  }

  /**
   * Completa il login dopo verifica 2FA
   * I cookie httpOnly sono già stati impostati dal backend
   */
  const completeLogin = async (data) => {
    const { user: userData, tenant: tenantData } = data

    console.log('[AuthContext] completeLogin called')

    // Cookie già impostati dal backend — aggiorna solo lo state React
    setUser(userData)
    setTenant(tenantData)
    setLoading(false)

    // FASE 5: Se l'utente è responsabile, carica organization
    if (userData.role === 'responsabile') {
      await loadOrganization()
    }

    // Se ha completato 2FA durante login, non ha bisogno di setup
    setNeeds2FASetup(false)
    twoFACheckDone.current = true // Marca come fatto

    // Push: se nel browser esiste già una subscription (residuo da utente
    // precedente sullo stesso device), riassociala all'utente corrente.
    // Idempotente: il backend fa upsert by endpoint.
    try {
      const { syncPushSubscriptionWithCurrentUser } = await import('../hooks/usePushNotifications')
      syncPushSubscriptionWithCurrentUser({ swallowErrors: true }) // fire-and-forget
    } catch { /* non-blocking */ }

    return { success: true }
  }

  /**
   * Logout — chiama il backend che cancella i cookie httpOnly
   *
   * IMPORTANTE: prima di cancellare i cookie facciamo cleanup della
   * PushSubscription del browser, altrimenti le notifiche push
   * continuerebbero ad arrivare anche dopo il logout.
   */
  const logout = async () => {
    console.log('[AuthContext] Logging out')

    // Cleanup push subscription PRIMA del logout backend
    // (serve cookie valido per chiamare DELETE /push-subscriptions)
    try {
      const { cleanupPushSubscription } = await import('../hooks/usePushNotifications')
      await cleanupPushSubscription({ swallowErrors: true })
    } catch (err) {
      console.warn('[AuthContext] cleanup push subscription failed:', err.message)
    }

    // Disconnetti Socket.IO esplicitamente
    try {
      const { disconnectSocket } = await import('../hooks/useSocket')
      disconnectSocket()
    } catch (err) {
      console.warn('[AuthContext] disconnect socket failed:', err.message)
    }

    try {
      await api.post('/auth/logout') // Il backend cancella i cookie httpOnly
    } catch (err) {
      console.error('[AuthContext] Logout API error:', err)
    }
    setUser(null)
    setTenant(null)
    setOrganization(null) // FASE 5: Reset organization
    setNeeds2FASetup(false)
    twoFACheckDone.current = false // Reset per prossimo login
  }

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }))
  }

  const updateTenant = (updates) => {
    setTenant(prev => ({ ...prev, ...updates }))
  }

  /**
   * FASE 5: Aggiorna organization
   */
  const updateOrganization = (updates) => {
    setOrganization(prev => ({ ...prev, ...updates }))
  }

  /**
   * Marca 2FA come configurato
   */
  const mark2FAConfigured = () => {
    setNeeds2FASetup(false)
    twoFACheckDone.current = true
  }

  // Helper per ruoli
  const isCoordinatore = () => user?.role === 'coordinatore'
  const isEducatore = () => user?.role === 'educatore'
  const isResponsabile = () => user?.role === 'responsabile' // FASE 5

  return (
    <AuthContext.Provider value={{
      user,
      tenant,
      organization, // FASE 5
      loading,
      error,
      login,
      logout,
      checkAuth,
      updateUser,
      updateTenant,
      updateOrganization, // FASE 5
      setOrganization, // FASE 5
      isAuthenticated: !!user,
      isCoordinatore,
      isEducatore,
      isResponsabile, // FASE 5
      completeLogin,
      needs2FASetup,
      setNeeds2FASetup,
      mark2FAConfigured
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
